import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { apiLinks } from 'src/app/core/api-links/api-links';
import { emailRegex } from 'src/app/core/model/regex';
import { CommonService } from 'src/app/providers/common.service';

@Component({
  selector: 'app-merchant-billing-dialog',
  templateUrl: './merchant-billing-dialog.component.html',
  styleUrls: ['./merchant-billing-dialog.component.scss']
})
export class MerchantBillingDialogComponent implements OnInit, OnDestroy {
  @Input() metaData: any;
  @Input() loader: boolean;
  @Input() showProgressBar: boolean;
  public showProgress: boolean = false;
  public clearStatusInterval: any;
  public progress: number = 0;
  public checkBoxControl = new FormControl(false);
  @Output() event = new EventEmitter();
  public isLoading: boolean = false;
  public customerInfo: any = localStorage.getItem('current_user') ? JSON.parse(localStorage.getItem('current_user')) : {};
  public emailControl: FormControl = new FormControl('', [Validators.required, Validators.pattern(emailRegex),this.loggedInEmailValidator()]);
  public diffEmail: boolean = false;
  public submissionError: boolean = false;
  public MODAL_CONFIGS: ModalConfig[] = [];
  @Input() action: string;
  config: ModalConfig | undefined;
  constructor(
    public activeModal: NgbActiveModal,
    private common: CommonService,


  ) { }

  ngOnInit(): void {
    const config = [
      {
        key: 'save_price_confirm',
        title: 'Save Sell Rate',
        description:
          'This sell rate will be used as the default for Merchant Invoicing. You can set merchant-specific sell rates from the Merchant Configuration screen.',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
      },
      {
        key: 'bulk_enable',
        title: 'Enable Merchant Invoicing',
        description:
          "Your merchants won't be invoiced unless Merchant Invoicing is enabled. Do you want to enable it for all your merchants at this selling price?",
        note: 'The selling price can be edited for individual merchants later as well.',
        confirmLabel: 'Bulk Enable',
        cancelLabel: 'Do Later',
      },
      {
        key: 'bulk_enable_confirm',
        title: 'Enable Merchant Invoicing',
        description:
          `This action affects ${this.metaData?.pendingMerchants || 'several'} merchant record${this.metaData?.pendingMerchants ? this.metaData?.pendingMerchants > 1 ? 's':'' :'s'}. In order to prevent accidental clicks we ask you to confirm this operation by entering your email address.`,
        requireEmail: true,
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        showProgressBar: true,
      },
      {
        key: 'bulk_update',
        title: 'Bulk Update Selling Price',
        description:
          `Are you sure you want to bulk update? The highlighted value will be applied to ${this.metaData?.totalMerchants > 1 ? `all ${this.metaData?.totalMerchants} merchants` : `${this.metaData?.totalMerchants} merchant` }.`,
        confirmLabel: 'Update',
        cancelLabel: 'Cancel',
        showCheckbox: true,
        checkboxLabel: 'Set this as the default price',
        showProgressBar: true,
      },
      {
        key: 'update_sell_price_confirm',
        title: 'Default Pricing',
        description:
          'This value will be set as the default for Merchant Invoicing and applied to all newly created merchants.',
        confirmLabel: 'Save',
        cancelLabel: 'Cancel',
      },
      {
        key: 'bulk_update_failed_ask_bulk_enable',
        title: 'Bulk Enable Merchant Invoicing',
        description: 'Billing is currently not enabled for the merchants. To proceed with the bulk update, billing must be activated for each merchant. Would you like to enable billing for all merchants now?',
        confirmLabel: 'Bulk Enable',
        cancelLabel: 'Cancel',
      },
      {
        key: 'remove_ach_processor',
        title: 'Remove ACH',
        description: 'All recurring ACH transactions will be canceled, and you will no longer be able to process ACH payments. If you wish to proceed, please enter your email below.',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel', 
        requireEmail: true,
      },
      {
        key: 'disable_register_lite',
        title: 'Disable Register Lite',
        description: 'Register Lite will be disabled for this store. If you wish to proceed, please enter your email below.',
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        requireEmail: true,
      },
      {
        key: 'iso_contract_update',
        title: 'Warning',
        description:
          `Changing an ISO’s pricing will affect all of their merchant’s pricing if they are using the merchant invoicing feature. The rates you set here for the ISO will override the rates for its merchants. Please consult with the ISO before making any changes.`,
        requireEmail: true,
        confirmLabel: 'Proceed',
        cancelLabel: 'Cancel',
        showProgressBar: true,
      },
    ];
    this.MODAL_CONFIGS = config;
    this.config = this.MODAL_CONFIGS.find(c => c.key === this.action);
  }
  isDisabled(): boolean {
    if (this.config.requireEmail) {
      return this.emailControl.invalid || this.isLoading;
    }
    return this.isLoading;
  }

  close() {
    this.activeModal.close(`${this.action}_close`)
  }

  submit() {
    if (this.config?.requireEmail) {
      if (this.emailControl.invalid) {
        this.isLoading = false;
        this.submissionError = true;
        return;
      }
      this.submissionError = false;
    }
    if (this.loader) {
      this.isLoading = true;
      this.showProgress = this.config.showProgressBar;
      if (this.showProgress) {
        this.getBillingStatus();
      }
      this.event.emit({ action: 'submit', checked: this.checkBoxControl.value, email:this.emailControl.valid });
      return;
    }
    this.activeModal.close(`${this.action}_submit`)
  }

  getBillingStatus = () => {
    this.clearStatusInterval = setInterval(async () => {
      try {
        const resData = await this.common.postCommonHttpClient(apiLinks.merchantBilling.billingStatus, { jobId: this.metaData.jobId }).toPromise();
        if (resData.data.bulkUploadStatus) {
          const data = resData.data.bulkUploadStatus
          this.progress = Math.floor((Number(data.completed_count) / Number(data.total_merchants)) * 100);
          if (this.progress == 100) {
            clearInterval(this.clearStatusInterval);
          }
        }
      } catch (error) {
        clearInterval(this.clearStatusInterval);
      }
    }, 1000 * 3);
  }

  getUploadMessage(): string {
    if (this.progress < 100) {
      return 'Processing...';
    } else {
      return 'Completed Successfully.';
    }
  }

  // Logged-in email validator
  loggedInEmailValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      // Only validate if metaData flag is true and logged email exists
      const loggedEmail = this.customerInfo?.email;
      if (this.metaData?.validateCurrentEmail && loggedEmail) {
        if (control.value && control.value !== loggedEmail) {
          return { emailMismatch: true };
        }
      }
      return null; // Valid
    };
  }

  ngOnDestroy(): void {
    clearInterval(this.clearStatusInterval);
  }
}

export interface ModalConfig {
  key: string;
  title: string;
  description?: string;
  note?: string;
  confirmLabel: string;
  cancelLabel: string;
  requireEmail?: boolean;
  showCheckbox?: boolean;
  checkboxLabel?: string;
  showProgressBar?: boolean;
}